/* Heirloom Services — numbered chapters (I, II, III). Each chapter
   has a Roman numeral left, copy + price + book CTA right, separated
   by dotted caramel rules. Reads like a printed program of services. */

import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useSiteSettings } from "@/hooks/useSiteSettings";
import { tenantHref } from "@/lib/tenant";
import { EditorialSkeleton } from "../editorial/_EditorialSkeleton";

const ROMAN = ["I", "II", "III", "IV", "V", "VI", "VII", "VIII", "IX", "X"];

function priceFrom(svc) {
  const tiers = Array.isArray(svc?.tiers) ? svc.tiers : [];
  const tp = tiers.map(t => Number(t?.price)).filter(n => Number.isFinite(n) && n > 0);
  if (tp.length) return Math.min(...tp);
  const n = Number(svc?.price_from ?? svc?.starting_price ?? svc?.price);
  return Number.isFinite(n) && n > 0 ? n : null;
}

export default function HeirloomServices() {
  const { settings } = useSiteSettings();
  const studioName = settings?.studio_name || "Studio";
  const [services, setServices] = useState(null);
  useEffect(() => {
    api.get("/services").then(r => setServices(Array.isArray(r.data) ? r.data : [])).catch(() => setServices([]));
    document.title = `${studioName} — The Program`;
  }, [studioName]);

  if (services === null) return <EditorialSkeleton eyebrow="Binding the program." />;

  return (
    <div data-testid="heirloom-services" style={{ background: "var(--tenant-bg, #F8F2EA)", color: "var(--tenant-fg, #2E2419)" }}>
      <section className="px-6 lg:px-16 pt-20 pb-12 text-center">
        <div className="text-[10px] uppercase tracking-[0.5em] mb-4" style={{ color: "var(--tenant-accent)" }}>
          The Program of Sittings
        </div>
        <h1 className="italic" style={{ fontFamily: "var(--tenant-serif)", fontSize: "clamp(2.5rem, 5vw, 4rem)" }}>
          Chapters, by occasion.
        </h1>
      </section>

      <div className="max-w-3xl mx-auto px-6 lg:px-16 pb-32 space-y-2">
        {services.map((svc, i) => {
          const p = priceFrom(svc);
          return (
            <article key={svc.id} id={svc.id || svc.slug}
              className="grid grid-cols-[80px_1fr_auto] gap-6 items-start py-10 border-b"
              style={{ borderColor: "color-mix(in srgb, var(--tenant-accent) 35%, transparent)", borderStyle: "dotted" }}>
              <div className="text-5xl italic text-right pt-1" style={{ fontFamily: "var(--tenant-serif)", color: "var(--tenant-accent)" }}>
                {ROMAN[i] || (i + 1)}
              </div>
              <div>
                <h2 className="italic text-2xl mb-3" style={{ fontFamily: "var(--tenant-serif)" }}>{svc.name}</h2>
                {(svc.description || svc.short_description) && (
                  <p className="text-sm leading-relaxed mb-4 max-w-lg" style={{ color: "var(--tenant-fg-soft)" }}>{svc.description || svc.short_description}</p>
                )}
                <Link to={tenantHref("/booking")}
                  className="inline-block text-[10px] uppercase tracking-[0.42em] border px-4 py-2"
                  style={{ borderColor: "var(--tenant-fg)", color: "var(--tenant-fg)" }}>
                  Reserve Chapter {ROMAN[i] || (i + 1)} →
                </Link>
              </div>
              {p !== null && (
                <div className="text-right shrink-0">
                  <div className="text-[10px] uppercase tracking-[0.32em]" style={{ color: "var(--tenant-fg-mute)" }}>From</div>
                  <div className="italic text-3xl" style={{ fontFamily: "var(--tenant-serif)", color: "var(--tenant-accent)" }}>${p.toFixed(0)}</div>
                </div>
              )}
            </article>
          );
        })}
        {services.length === 0 && (
          <p className="text-center italic py-12" style={{ color: "var(--tenant-fg-soft)" }}>The program is being typeset.</p>
        )}
      </div>
    </div>
  );
}
